// App.js — WorksForYou website
//
// Responsibilities:
//   1. BOOTSTRAP. Fetch __config__ from FastAPI → API URL, prefix, version, bg.
//   2. WEBSITE DATA. Fetch /api/website ONCE — the assembler's full dict with
//      every page (home, how-it-works, small automations, complex systems,
//      contact). Navigating between pages makes NO further backend calls;
//      a browser refresh re-fetches everything → picks up yaml/image edits.
//   3. ROUTING. React Router with basename = the backend's PREFIX. The right
//      page component is mounted for each route; all pages read their data
//      from <ConfigContext> rather than fetching anything themselves.

import React, { useState, useEffect, useMemo, createContext } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import axios from 'axios';

import { resolveBilingualTree, detectLanguage } from './myReactLib/MainBlocks';
import BackendPageForHP      from './pages/HomePage/BackendPageForHP';
import BackendPageForHIW     from './pages/HowItWorks/BackendPageForHIW';
import BackendPageForSA      from './pages/SmallAutomations/BackendPageForSA';
import BackendPageForCS      from './pages/ComplexSystems/BackendPageForCS';
import BackendPageForAbout   from './pages/About/BackendPageForAbout';
import BackendPageForContact from './pages/Contact/BackendPageForContact';
import BackendPageForPrivacy from './pages/_Privacy_and_ToS/BackendPageForPrivacy';
import BackendPageForTerms   from './pages/_Privacy_and_ToS/BackendPageForTerms';
import BackendPageForDataDeletion from './pages/_Privacy_and_ToS/BackendPageForDataDeletion';

// Any descendant can `useContext(ConfigContext)` to read config + page data.
export const ConfigContext = createContext({});

function App() {
  const [config, setConfig]             = useState(null);
  const [configError, setConfigError]   = useState(null);
  const [websiteData, setWebsiteData]   = useState(null);  // the assembler's full dict (all pages)
  const [websiteError, setWebsiteError] = useState(null);
  // Active UI language. Auto-detected from the browser; setLang lets a future
  // on-page switcher flip EN↔IT instantly (data for both is already loaded).
  const [lang, setLang]                 = useState(detectLanguage());

  // 1) Fetch __config__ once on mount.
  useEffect(() => {
    fetch('./__config__')
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(setConfig)
      .catch((err) => setConfigError(`Failed to load config: ${err.message}`));
  }, []);

  // 2) Apply the page background from the backend (with cache-buster).
  //    No BACKGROUND_IMAGE_URL (no background.<ext> file) → explicitly clear any
  //    image so the body shows the default uniform color (--color-bg-page).
  useEffect(() => {
    if (!config) return;
    if (!config.BACKGROUND_IMAGE_URL) {
      document.body.style.backgroundImage = 'none';
      return;
    }
    const url = config.BACKGROUND_IMAGE_URL;
    const sep = url.includes('?') ? '&' : '?';
    document.body.style.backgroundImage      = `url("${url}${sep}t=${Date.now()}")`;
    document.body.style.backgroundSize       = 'cover';
    document.body.style.backgroundAttachment = 'fixed';
    document.body.style.backgroundPosition   = 'center';
  }, [config]);

  // 3) Fetch the WHOLE website dict once we have the API URL. One call, all
  //    pages — navigation afterwards is purely client-side (instant).
  //    NOTE: guard on `config` ONLY — never on config.PREFIX. In prod the
  //    prefix is "" (root deploy), which is falsy; guarding on it would skip
  //    this fetch entirely and leave every page stuck on "Loading…".
  useEffect(() => {
    if (!config || !config.API_URL) return;
    axios.get(`${config.API_URL}/website`)
      .then((res) => setWebsiteData(res.data))
      .catch((err) => setWebsiteError(err.message || 'Failed to load website data'));
  }, [config]);

  // Resolve the bilingual payload down to the active language once per change.
  // Components keep seeing the same { text, format } shape they always have.
  const websiteResolved = useMemo(
    () => (websiteData ? resolveBilingualTree(websiteData, lang) : null),
    [websiteData, lang]
  );

  if (configError)   return <div className="w4u-error">{configError}</div>;
  if (!config)       return <div className="w4u-loading">Loading WorksForYou…</div>;
  if (websiteError)  return <div className="w4u-error">Failed to load page data: {websiteError}</div>;
  if (!websiteResolved) return <div className="w4u-loading">Loading…</div>;

  // Per-page slices (kept under the names the components already use).
  const homepageData         = websiteResolved.home_page;
  const howItWorksData       = websiteResolved.how_it_works_page;
  const smallAutomationsData = websiteResolved.small_automations_page;
  const complexSystemsData   = websiteResolved.complex_systems_page;
  const contactData          = websiteResolved.contact_page;

  return (
    <ConfigContext.Provider value={{ config, websiteData: websiteResolved, homepageData, howItWorksData, smallAutomationsData, complexSystemsData, contactData, lang, setLang }}>
      <Router basename={config.PREFIX}>
        <Routes>
          <Route path="/"                   element={<BackendPageForHP />} />
          <Route path="/homepage"           element={<BackendPageForHP />} />
          <Route path="/how-it-works"       element={<BackendPageForHIW />} />
          <Route path="/small-automations"  element={<BackendPageForSA />} />
          <Route path="/complex-systems"    element={<BackendPageForCS />} />
          <Route path="/about"              element={<BackendPageForAbout />} />
          <Route path="/contact"            element={<BackendPageForContact />} />
          <Route path="/privacy"            element={<BackendPageForPrivacy />} />
          <Route path="/terms"              element={<BackendPageForTerms />} />
          <Route path="/data-deletion"      element={<BackendPageForDataDeletion />} />
        </Routes>
      </Router>
    </ConfigContext.Provider>
  );
}

export default App;
